import { useSyncExternalStore } from 'react'

/*
 * How a selected tool button looks (owner, 2026-10-13: the ring "not very nice"; alternatives were
 * compared on the tablet in the settings panel's old Buttons group). The owner settled on the themed
 * sliding backing ('slide'), so that is the look on every page and nothing is stored any more (an
 * old stored choice is ignored). The other looks stay as code paths: `setSelectedLook` still
 * switches every WorldButton and ColorBar at once (for a console or a lab page), for this session.
 */

export type SelectedLook = 'ring' | 'pressed' | 'tint' | 'dot' | 'slide' | 'well' | 'flat' | 'underline' | 'glow' | 'tidy'

export const SELECTED_LOOKS: readonly { id: SelectedLook; label: string }[] = [
  { id: 'ring', label: 'Ring' },
  { id: 'pressed', label: '1. Pressed in' },
  { id: 'tint', label: '2. Tinted face' },
  { id: 'dot', label: '3. Dot below' },
  { id: 'slide', label: '4. Sliding backing' },
  { id: 'well', label: '4a. Recessed well' },
  { id: 'flat', label: '4b. Flat tint' },
  { id: 'underline', label: '4c. Sliding underline' },
  { id: 'glow', label: '4d. Glow puck' },
  { id: 'tidy', label: '5. Tidier ring' },
]

/** The looks drawn by the bar as one shape that slides to the selected button (WorldBar). */
export const SLIDING_LOOKS: ReadonlySet<SelectedLook> = new Set(['slide', 'well', 'flat', 'underline', 'glow'])

/** The look every page shows (owner, 2026-10-13: the sliding backing). */
export const DEFAULT_SELECTED_LOOK: SelectedLook = 'slide'

const listeners = new Set<() => void>()
let current: SelectedLook = DEFAULT_SELECTED_LOOK

/** Switches the look for this session (not remembered). */
export function setSelectedLook(look: SelectedLook) {
  if (look === current) return
  current = look
  for (const l of listeners) l()
}

export function useSelectedLook(): SelectedLook {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => listeners.delete(l) },
    () => current,
    () => DEFAULT_SELECTED_LOOK,
  )
}

/** `color` (#rrggbb) mixed `t` of the way into white. */
export function tintToward(color: string, t: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(color)
  if (!m) return '#ffffff'
  const n = parseInt(m[1], 16)
  const ch = (shift: number) => Math.round(255 - (255 - ((n >> shift) & 255)) * t)
  return `rgb(${ch(16)}, ${ch(8)}, ${ch(0)})`
}

/** Is `color` (#rrggbb) pale (so a mark in it would not show on a white bar)? */
export function isPale(color: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(color)
  if (!m) return false
  const n = parseInt(m[1], 16)
  const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255
  return lum > 0.82
}

/** Parses #rrggbb or rgb()/rgba() into [r, g, b] (0..255), or null. */
function parseColor(color: string): [number, number, number] | null {
  const hex = /^#?([0-9a-f]{6})$/i.exec(color.trim())
  if (hex) {
    const n = parseInt(hex[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i.exec(color.trim())
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null
}

const luminance = ([r, g, b]: [number, number, number]) => (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255

/**
 * The colour of a bar's sliding mark (backing, well, pale flat tint), from the page's theme: its
 * accent, deepened toward ink on a light bar and lifted toward white on a dark one, so Sand's mark
 * is a warm sand, Sludge's a pink, Moon slime's a pale violet on its dark bar.
 */
export function barMarkColor(accent: string | undefined, background: string, alpha: number): string {
  const bar = parseColor(background) ?? [255, 255, 255]
  const dark = luminance(bar) < 0.45
  const a = (accent && parseColor(accent)) || (dark ? [255, 255, 255] : [16, 32, 48])
  // Pull the accent away from the bar's own lightness so the mark always shows.
  const toward = dark ? [255, 255, 255] : [16, 32, 48]
  const t = dark ? 0.45 : luminance(a as [number, number, number]) > 0.7 ? 0.35 : 0.1
  const mixed = a.map((v, i) => Math.round(v + (toward[i] - v) * t))
  return `rgba(${mixed[0]}, ${mixed[1]}, ${mixed[2]}, ${dark ? Math.min(1, alpha * 1.6) : alpha})`
}
